import React from 'react';
import { useTranslation } from 'react-i18next';
import { AllCommunityModule, ColDef, ModuleRegistry } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { themeZafire } from '@zafire/ag-grid-enterprise';
import { PartnerSearchByBrResponse } from 'pco-product-service-internal-consumer';
import { getPartnerKey } from '../utils/partnerDisplayUtils';
import { EmptyStateOverlay } from '../../../EmptyStateOverlay';
import { ZfDividerTitle } from 'components/common/ZfDividerTitle';

ModuleRegistry.registerModules([AllCommunityModule]);

const selectedPartnersDefaultColDef: ColDef<PartnerSearchByBrResponse> = {
  sortingOrder: ['asc', 'desc'],
};

// Room the rest of the Edit product dialog needs: chrome, partner search and close section. Sizing the
// grid off the viewport minus this lets the dialog grow to its own 90dvh cap instead of hugging a fixed
// grid. Deliberately an over-estimate, so an error shortens the grid rather than pushing the close
// button out of the dialog. Smaller than the Safe Deposit Box figure: no configuration fields here.
const DIALOG_HEIGHT_WITHOUT_GRID = '420px';

const GRID_STYLE: React.CSSProperties = {
  width: '100%',
  height: `calc(90dvh - ${DIALOG_HEIGHT_WITHOUT_GRID})`,
  minHeight: 200,
  display: 'flex',
  flexDirection: 'column',
};

type Props = {
  columnDefs: ColDef<PartnerSearchByBrResponse>[];
  selectedPartners: PartnerSearchByBrResponse[];
  productName: string;
};

export const SelectedPartnersGrid = React.memo(({ columnDefs, selectedPartners, productName }: Props) => {
  const { t } = useTranslation();
  return (
    <div data-testid="selected-partners-grid" style={GRID_STYLE}>
      <ZfDividerTitle label={`${productName} users`} />
      <div style={{ flex: 1, minHeight: 0 }}>
        <AgGridReact<PartnerSearchByBrResponse>
          rowData={selectedPartners}
          columnDefs={columnDefs}
          defaultColDef={selectedPartnersDefaultColDef}
          getRowId={(params) => getPartnerKey(params.data)}
          suppressCellFocus
          suppressRowHoverHighlight
          theme={themeZafire}
          noRowsOverlayComponent={EmptyStateOverlay}
          noRowsOverlayComponentParams={{
            icon: 'search_off',
            title: t('pb.selectedpartnersgrid.emptystate.title', 'There are no {{productName}} users to display', {
              productName,
            }),
            description: t('pb.selectedpartnersgrid.emptystate.description', 'Start by adding a partner'),
          }}
        />
      </div>
    </div>
  );
});

SelectedPartnersGrid.displayName = 'SelectedPartnersGrid';
